import {Trans} from '@lingui/macro';
import React, {FC, ReactNode, useCallback, useEffect, useMemo, useState} from 'react';
import styled from 'styled-components';

import {Handler, HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {BoxedInput} from '../../../../../libs/shared/ui/src/components/boxedInputs/boxedInput';
import {
  BoxedInputElement,
  BoxedInputElementTypesEnum,
} from '../../../../../libs/shared/ui/src/components/boxedInputs/boxedInputElement';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {InlineLinkButton} from '../../../../../libs/shared/ui/src/components/buttons/inlineLinkButton';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {openInBrowser} from '../../../../../srcCommon/browser/openInBrowser';
import {usePrevious, useToggle} from '../../../../../srcCommon/helpers/hookHelpers';
import {Dialog} from '../../../../components/common/dialogs/dialog';
import {DialogFooter} from '../../../../components/common/dialogs/dialogFooter';
import {DialogHeader} from '../../../../components/common/dialogs/dialogHeader';
import {Paragraph} from '../../../../components/common/paragraph';
import {TestDataSidebar} from '../../../editors/expressions/testDataSidebar';
import {PreviewerEditorState} from '../../../editors/previewerEditor/previewerEditorState';
import {PreviewerEditorStateActions} from '../../../editors/previewerEditor/usePreviewerEditorState';

/*
 * Constants.
 */

const dialogWidth = 400; // px.

const ALLOWED_PROTOCOLS = ['https:', 'http:'];

/*
 * Styles.
 */

const StyledDialogContent = styled.div`
  padding: 20px;
`;

const StyledParagraph = styled(Paragraph)`
  margin-top: 20px;
`;

const StyledButtons = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
  column-gap: 10px;
`;

/*
 * Component.
 */

interface PreviewerTestDataSidebarProps {
  previewerEditorState: PreviewerEditorState;
  actions: PreviewerEditorStateActions;
  isReadOnly: boolean;
}

export const PreviewerTestDataSidebar: FC<PreviewerTestDataSidebarProps> = ({
  previewerEditorState,
  actions,
  isReadOnly,
}) => {
  const {previewerState, hasDryRunTestData} = previewerEditorState;
  const {domain, exampleUrl} = previewerState;
  const [isEditing, toggleEditing] = useToggle();
  const previousExampleUrl = usePrevious(exampleUrl);

  const onUpdate = useCallback(
    (value: string) => {
      actions.onUpdateExampleUrl(value);
      toggleEditing();
    },
    [actions, toggleEditing],
  );

  // Dry run again when example URL changes.
  useEffect(() => {
    if (exampleUrl !== previousExampleUrl) {
      actions.onExecDryRun();
    }
  }, [actions, exampleUrl, previousExampleUrl]);

  return (
    <>
      <TestDataSidebar
        summary={renderSummary(exampleUrl)}
        hasDryRunTestData={hasDryRunTestData}
        isReadOnly={isReadOnly}
        onEdit={toggleEditing}
        onDryRun={actions.onExecDryRun}
      />
      {isEditing && (
        <PreviewerTestDataDialog
          domain={domain}
          exampleUrl={exampleUrl}
          onUpdate={onUpdate}
          onRequestClose={toggleEditing}
        />
      )}
    </>
  );
};

function renderSummary(exampleUrl: string | undefined): ReactNode {
  if (!exampleUrl) {
    return undefined;
  }

  try {
    const url = new URL(exampleUrl);
    const label = `${url.host}${url.pathname}${url.search}`;

    return <InlineLinkButton onClick={() => openInBrowser(exampleUrl)}>{label}</InlineLinkButton>;
  } catch (err) {
    return exampleUrl;
  }
}

interface PreviewerTestDataDialogProps {
  domain: string;
  exampleUrl?: string;
  onUpdate: HandlerOf<string>;
  onRequestClose: Handler;
}

const PreviewerTestDataDialog: FC<PreviewerTestDataDialogProps> = ({
  domain,
  exampleUrl,
  onUpdate,
  onRequestClose,
}) => {
  const [value, onChange] = useState(exampleUrl ?? '');
  const [shouldDisplayError, setDisplayError] = useState(false);

  const errorMessage = useMemo(() => {
    if (!value) {
      return undefined;
    }

    try {
      const url = new URL(value);

      if (!ALLOWED_PROTOCOLS.includes(url.protocol)) {
        return <Trans id="previewer-testdata-error-protocol">Protocol must be HTTP or HTTPS</Trans>;
      }

      if (domain && url.hostname !== domain) {
        return <Trans id="previewer-testdata-error-domain">Must match selected domain: {domain}</Trans>;
      }

      return undefined;
    } catch (err) {
      if (!value.startsWith('http')) {
        return <Trans id="previewer-testdata-error-prefix">Must start with http:// or https://</Trans>;
      }

      return <Trans id="previewer-testdata-error-url">Must be a valid URL</Trans>;
    }
  }, [value, domain]);

  const hasError = Boolean(errorMessage);
  const isDisabled = !value || (hasError && shouldDisplayError);

  const onUpdateClick = useCallback(() => {
    setDisplayError(true);

    if (value && !hasError) {
      onUpdate(value);
    }
  }, [hasError, onUpdate, value]);

  const placeholder = useMemo(() => {
    const placeholderDomain = domain || 'backoffice.company.com';
    return `https://${placeholderDomain}/object/123`;
  }, [domain]);

  return (
    <Dialog onRequestClose={onRequestClose} width={dialogWidth}>
      <DialogHeader
        title={<Trans id="previewer-testdata-title">Example URL</Trans>}
        onRequestClose={onRequestClose}
      />
      <StyledDialogContent>
        <BoxedInput
          size={VisualSizesEnum.LARGE}
          hasError={shouldDisplayError && hasError}
          errorMessage={shouldDisplayError && errorMessage}
          noMargin
          shouldAutoFocus
        >
          {(childProps) => (
            <BoxedInputElement
              {...childProps}
              type={BoxedInputElementTypesEnum.TEXT}
              placeholder={placeholder}
              value={value ?? ''}
              onChange={onChange}
            />
          )}
        </BoxedInput>
        <StyledParagraph>
          <Trans id="previewer-testdata-help">
            Your dynamic variables will be evaluated using this URL. It must use the domain of your previewer.
          </Trans>
        </StyledParagraph>
      </StyledDialogContent>
      <DialogFooter>
        <StyledButtons>
          <Button variant={ButtonVariantsEnum.SECONDARY} onClick={onRequestClose}>
            <Trans id="previewer-testdata-cancel">Cancel</Trans>
          </Button>
          <Button variant={ButtonVariantsEnum.PRIMARY} onClick={onUpdateClick} isDisabled={isDisabled}>
            <Trans id="previewer-testdata-dryrun">Dry run</Trans>
          </Button>
        </StyledButtons>
      </DialogFooter>
    </Dialog>
  );
};
